import React, { useState, useEffect } from 'react';
import { posts as initialPosts } from '../../api/mockData';
import PostCard from '../../components/PostCard';
import { message, Tag } from 'antd';
import './index.css';

function Home() {
  const [posts, setPosts] = useState([]);
  const [isAuthor, setIsAuthor] = useState(true);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPosts: 0,
    techPosts: 0,
    programmingPosts: 0,
    webPosts: 0
  });

  // 加载文章
  useEffect(() => {
    loadAndMergePosts();
    
    const handlePostsUpdated = () => {
      loadAndMergePosts();
    };

    window.addEventListener('blogPostsUpdated', handlePostsUpdated);

    return () => {
      window.removeEventListener('blogPostsUpdated', handlePostsUpdated);
    };
  }, []);

  const loadAndMergePosts = () => {
    setLoading(true);
    try {
      const savedPosts = JSON.parse(localStorage.getItem('blogPosts')) || [];
      const mockPosts = initialPosts || [];
      
      const postIds = new Set();
      const mergedPosts = [];
      
      savedPosts.forEach(post => {
        if (post.id && !postIds.has(post.id)) {
          postIds.add(post.id);
          mergedPosts.push({
            ...post,
            isUserPost: true
          });
        }
      });
      
      mockPosts.forEach(post => {
        if (post.id && !postIds.has(post.id)) {
          postIds.add(post.id);
          mergedPosts.push({
            ...post,
            isUserPost: false
          });
        }
      });
      
      mergedPosts.sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateB - dateA;
      });
      
      setPosts(mergedPosts);
      
      // 计算统计数据
      const techPosts = mergedPosts.filter(post => 
        post.tags?.includes('技术') || 
        post.tags?.includes('tech') || 
        post.tags?.includes('前端')
      ).length;
      
      const programmingPosts = mergedPosts.filter(post => 
        post.tags?.includes('编程') || 
        post.tags?.includes('编程语言') || 
        post.tags?.includes('javascript')
      ).length;
      
      const webPosts = mergedPosts.filter(post => 
        post.tags?.includes('web') || 
        post.tags?.includes('web开发') || 
        post.tags?.includes('前端开发')
      ).length;
      
      setStats({
        totalPosts: mergedPosts.length,
        techPosts,
        programmingPosts,
        webPosts
      });
    } catch (error) {
      console.error('加载文章失败:', error);
      setPosts(initialPosts || []);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      const savedPosts = JSON.parse(localStorage.getItem('blogPosts')) || [];
      const updatedPosts = savedPosts.filter(post => post.id !== postId);
      localStorage.setItem('blogPosts', JSON.stringify(updatedPosts));
      setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
      window.dispatchEvent(new Event('blogPostsUpdated'));
      message.success('文章删除成功');
      return true;
    } catch (error) {
      console.error('删除文章失败:', error);
      message.error('删除文章失败');
      throw error;
    }
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              欢迎来到我的<span className="highlight">博客</span>
            </h1>
            <p className="hero-subtitle">
              这是一个思想、创意和故事得以呈现的地方。
              探索我们关于技术、编程和Web开发的最新文章。
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">{stats.totalPosts}</span>
                <span className="stat-label">篇文章</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{stats.techPosts}</span>
                <span className="stat-label">技术文章</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{stats.programmingPosts}</span>
                <span className="stat-label">编程文章</span>
              </div>
            </div>
            <div className="hero-cta">
              <a href="#posts" className="btn btn-primary">
                探索文章
                <span className="btn-icon">→</span>
              </a>
              <a href="/write" className="btn btn-secondary">
                开始写作
                <span className="btn-icon">✏️</span>
              </a>
            </div>
          </div>
          <div className="hero-image">
            <div className="code-snippet">
              <pre>
                <code>
{`// 欢迎来到代码世界
function Blog() {
  const ideas = [];
  const creativity = Infinity;
  
  return (
    <div className="blog">
      <Article 
        title="思想的交汇处"
        content="这里记录着每一次思考的痕迹"
      />
    </div>
  );
}`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Categories */}
      <div className="categories-section">
        <div className="container">
          <h2 className="section-title">热门分类</h2>
          <div className="categories-grid">
            <div className="category-card">
              <div className="category-icon">💻</div>
              <h3>前端开发</h3>
              <p>React, Vue, JavaScript等前端技术</p>
              <span className="category-count">{stats.webPosts} 篇文章</span>
            </div>
            <div className="category-card">
              <div className="category-icon">🚀</div>
              <h3>编程语言</h3>
              <p>JavaScript, Python, Java等语言特性</p>
              <span className="category-count">{stats.programmingPosts} 篇文章</span>
            </div>
            <div className="category-card">
              <div className="category-icon">🎯</div>
              <h3>技术分享</h3>
              <p>最新技术趋势和实践经验分享</p>
              <span className="category-count">{stats.techPosts} 篇文章</span>
            </div>
            <div className="category-card">
              <div className="category-icon">📚</div>
              <h3>学习笔记</h3>
              <p>学习过程中的心得和总结</p>
              <span className="category-count">持续更新</span>
            </div>
          </div>
        </div>
      </div>

      {/* Latest Posts */}
      <div id="posts" className="posts-section">
        <div className="container">
          <div className="posts-header">
            <div>
              <h2 className="section-title">最新文章</h2>
              <p className="section-subtitle">探索最新发布的技术文章和教程</p>
            </div>
            {isAuthor && (
              <div className="author-badge">
                <span className="author-icon">👑</span>
                <span>作者模式已启用</span>
                <div className="author-tooltip">
                  * 将鼠标悬停在文章上可显示删除按钮
                </div>
              </div>
            )}
          </div>
          
          {loading ? (
            <div className="loading-posts">
              <div className="spinner"></div>
              <p>正在加载文章...</p>
            </div>
          ) : posts.length > 0 ? (
            <>
              <div className="post-filters">
                <button className="filter-btn active">全部</button>
                <button className="filter-btn">技术</button>
                <button className="filter-btn">编程</button>
                <button className="filter-btn">前端</button>
                <button className="filter-btn">教程</button>
              </div>
              
              <div className="post-card-grid">
                {posts.map(post => (
                  <PostCard 
                    key={post.id} 
                    post={post}
                    isAuthor={isAuthor && post.isUserPost}
                    onDelete={handleDeletePost}
                  />
                ))}
              </div>
              
              <div className="posts-footer">
                <p>显示 {posts.length} 篇文章</p>
                <a href="/archive" className="view-all">
                  查看全部文章
                  <span className="arrow">→</span>
                </a>
              </div>
            </>
          ) : (
            <div className="no-posts">
              <div className="no-posts-icon">📝</div>
              <h3>还没有文章</h3>
              <p>成为第一个分享想法的人！</p>
              <a href="/write" className="btn btn-primary">
                开始写作
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Call to Action */}
      <div className="cta-section">
        <div className="container">
          <div className="cta-card">
            <h2>加入我们的社区</h2>
            <p>与志同道合的技术爱好者交流，分享你的见解和经验</p>
            <div className="cta-buttons">
              <a href="/write" className="btn btn-primary btn-large">
                ✏️ 开始写作
              </a>
              <a href="/about" className="btn btn-outline">
                ℹ️ 了解更多
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;